import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Alert,
  LinearProgress,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { Formik, Form, FieldArray, Field, FormikProps, getIn } from "formik";
import * as Yup from "yup";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  AllocationCategory,
  AllocationStatus,
  AllocationSubCategory,
  MetaItem,
  AllocationTag,
  ProjectDetailTabs,
} from "../types";
import {
  commonButtonProps,
  commonTextFieldProps,
  commonDateFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { useToast } from "../../../components/Toast";
import NavigationHeader from "../../../components/NavigationHeader";
import ReferenceSelectionFields from "../components/ReferenceSelectionFields";
import AllocationTagsSection from "../components/AllocationTagsSection";
import {
  AllocationCardEnum,
  PROJECT_TERMINOLOGY,
  ProjectTabEnum,
} from "../enums";
import { allocationApi, projectModuleApi } from "../services/apiService";
import { formatText } from "../../../utils/utils";
import { getMetaTypes, getSubCategories } from "../enums/metaTags";
import { Loading } from "../../../components/Loading";

// Form interfaces
interface AllocationFormData {
  category: AllocationCategory;
  subCategory: string;
  categoryId: string;
  referenceData?: any; // Selected reference object or custom data
  startDate: string;
  endDate?: string | null;
  durationMonths: string | "";
  meta: MetaItem[];
  allocationTags: AllocationTag[];
  projectId: string;
  companyId?: string;
  createdBy?: string;
}

interface AllocationFormValues {
  allocations: AllocationFormData[];
}

// Validation schema
const metaItemSchema = Yup.object().shape({
  type: Yup.string().required("Meta type is required"),
  id: Yup.string().optional(), // ID of referenced entity (for searchable selections)
  value: Yup.mixed().when("type", {
    is: (type: string) => type && type.trim() !== "",
    then: (schema) =>
      schema.required("Value is required when type is selected"),
    otherwise: (schema) => schema.optional(),
  }), // Required only when type is selected
});

const allocationTagSchema = Yup.object().shape({
  category1Type: Yup.string().required("Category 1 type is required"),
  category1Id: Yup.string().required("Category 1 ID is required"),
  category2Type: Yup.string().required("Category 2 type is required"),
  category2Id: Yup.string().required("Category 2 ID is required"),
});

const allocationSchema = Yup.object().shape({
  category: Yup.string()
    .oneOf(Object.values(AllocationCategory), "Invalid category")
    .required("Category is required"),
  subCategory: Yup.string().required("Sub-category is required"),
  categoryId: Yup.string().optional(),
  startDate: Yup.date()
    .required("Start date is required")
    .typeError("Please enter a valid date"),
  durationMonths: Yup.string()
    .optional()
    .test(
      "is-valid-duration",
      "Duration must be between 1 and 100 months",
      function (value) {
        if (!value || value === "") return true; // Optional field
        const num = parseFloat(value);
        return !isNaN(num) && num >= 1 && num <= 100;
      }
    ),
  endDate: Yup.date().nullable().typeError("Please enter a valid date"),
  meta: Yup.array().of(metaItemSchema).optional(),
  allocationTags: Yup.array().of(allocationTagSchema).optional(),
});

// allow only 10 allocations at once
const validationSchema = Yup.object().shape({
  allocations: Yup.array()
    .of(allocationSchema)
    .min(1, "At least one allocation is required"),
});

// Map allocation type to category and subcategory
const getAllocationConfig = (allocationType: string | null) => {
  switch (allocationType) {
    case AllocationCardEnum.VEHICLE.type:
      return {
        category: AllocationCategory.VEHICLE,
        subCategory: AllocationSubCategory.BUS, // Default to BUS, user can change
      };
    case AllocationCardEnum.RESOURCE.type:
      return {
        category: AllocationCategory.RESOURCE,
        subCategory: AllocationSubCategory.DRIVER,
      };
    default:
      return {
        category: AllocationCategory.VEHICLE,
        subCategory: AllocationSubCategory.BUS,
      };
  }
};

// Initial values will be created inside the component based on allocationType

const CreateAllocation: React.FC = () => {
  const navigate = useNavigate();
  const { projectId, superProjectId } = useParams<{
    projectId: string;
    superProjectId: string;
  }>();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const allocationType = searchParams.get("allocationType");
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const [projectDetails, setProjectDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProjectDetails = async () => {
    setIsLoading(true);
    try {
      const data = await projectModuleApi.getModule(projectId!);
      setProjectDetails(data);
    } catch (error) {
      showToast(
        `Failed to fetch ${PROJECT_TERMINOLOGY.LOWER_SINGULAR} details`,
        "error"
      );
      navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchProjectDetails();
  }, []);

  if (!projectId) {
    return <Navigate to="/" />;
  }

  if (isLoading) {
    return <Loading message="Loading..." />;
  }

  // Create initial values based on allocation type
  const allocationConfig = getAllocationConfig(allocationType);
  const initialAllocation: AllocationFormData = {
    category: allocationConfig.category,
    subCategory: allocationConfig.subCategory,
    categoryId: "",
    referenceData: null,
    startDate: projectDetails?.startDate
      ? new Date(projectDetails?.startDate).toISOString().split("T")[0]
      : "",
    endDate: null,
    durationMonths: "",
    meta: [],
    allocationTags: [],
    projectId: projectId,
    companyId: companyId,
    createdBy: localUser ? JSON.parse(localUser).id : "",
  };

  const initialValues: AllocationFormValues = {
    allocations: [{ ...initialAllocation }],
  };

  const goBack = () => {
    const params = `tab=${ProjectDetailTabs.ALLOCATIONS}&type=${allocationType}`;
    if (superProjectId) {
      navigate(
        `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?${params}`
      );
    } else {
      navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?${params}`);
    }
  };

  const handleSubmit = async (values: AllocationFormValues) => {
    setIsSubmitting(true);
    try {
      // Create allocations using API
      await allocationApi.bulkCreateAllocationWithTags(values.allocations);

      showToast("Allocations created successfully!", "success");
      goBack();
    } catch (error) {
      console.error("Error creating allocations:", error);
      showToast("Failed to create allocations. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReferenceChange = (
    allocationIndex: number,
    reference: any,
    customData: any,
    formik: FormikProps<AllocationFormValues>
  ) => {
    if (reference) {
      // Selected from existing options
      formik.setFieldValue(
        `allocations.${allocationIndex}.categoryId`,
        reference.id
      );
      formik.setFieldValue(
        `allocations.${allocationIndex}.referenceData`,
        reference
      );
    } else if (customData) {
      // Manual entry - keep categoryId blank
      formik.setFieldValue(`allocations.${allocationIndex}.categoryId`, "");
      formik.setFieldValue(
        `allocations.${allocationIndex}.referenceData`,
        customData
      );
    } else {
      // Clear selection
      formik.setFieldValue(`allocations.${allocationIndex}.categoryId`, "");
      formik.setFieldValue(
        `allocations.${allocationIndex}.referenceData`,
        null
      );
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <NavigationHeader
        title={`New ${allocationType?.charAt(0).toUpperCase()}${allocationType
          ?.slice(1)
          .toLowerCase()} Allocation`}
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: superProjectId
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          ...(superProjectId
            ? [
                {
                  label: ProjectTabEnum.DIVISIONS.label,
                  path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                },
                {
                  label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                  path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                },
              ]
            : []),
          {
            label: ProjectTabEnum.ALLOCATIONS.label,
            path: superProjectId
              ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.ALLOCATIONS}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.ALLOCATIONS}`,
          },
          {
            label: `New ${allocationType
              ?.charAt(0)
              .toUpperCase()}${allocationType
              ?.slice(1)
              .toLowerCase()} Allocation`,
          },
        ]}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          return (
            <Form>
              <Paper sx={{ p: 2, mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AssignmentIcon color="primary" />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, fontSize: "0.95rem" }}
                    >
                      {ProjectTabEnum.ALLOCATIONS.singular} Details
                    </Typography>
                  </Box>
                </Box>

                {isSubmitting && <LinearProgress sx={{ mb: 1.5 }} />}

                <FieldArray name="allocations">
                  {({ push, remove }) => (
                    <Box>
                      {formik.values.allocations.map((allocation, index) => (
                        <Card
                          key={index}
                          sx={{
                            mb: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <CardHeader
                            title={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 500, fontSize: "0.85rem" }}
                                >
                                  {ProjectTabEnum.ALLOCATIONS.singular} #
                                  {index + 1}
                                </Typography>
                                {formik.values.allocations.length > 1 && (
                                  <IconButton
                                    onClick={() => remove(index)}
                                    size="small"
                                    color="error"
                                    disabled={isSubmitting}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>
                            }
                            sx={{ pb: 1 }}
                          />
                          <CardContent sx={{ pt: 0, pb: 1 }}>
                            <Grid container spacing={1.5}>
                              {/* Sub-Category */}
                              <Grid item xs={12} md={2}>
                                <FormControl
                                  fullWidth
                                  error={
                                    getIn(
                                      formik.touched,
                                      `allocations.${index}.subCategory`
                                    ) &&
                                    Boolean(
                                      getIn(
                                        formik.errors,
                                        `allocations.${index}.subCategory`
                                      )
                                    )
                                  }
                                >
                                  <InputLabel
                                    sx={{ fontSize: "0.75rem" }}
                                    size="small"
                                  >
                                    {allocation.category ===
                                    AllocationCategory.VEHICLE
                                      ? `${formatText(
                                          AllocationCardEnum.VEHICLE.type
                                        )} Type`
                                      : `${formatText(
                                          AllocationCardEnum.RESOURCE.type
                                        )} Type`}
                                  </InputLabel>
                                  <Field
                                    as={Select}
                                    name={`allocations.${index}.subCategory`}
                                    label={
                                      allocation.category ===
                                      AllocationCategory.VEHICLE
                                        ? `${formatText(
                                            AllocationCardEnum.VEHICLE.type
                                          )} Type`
                                        : `${formatText(
                                            AllocationCardEnum.RESOURCE.type
                                          )} Type`
                                    }
                                    disabled={isSubmitting}
                                    {...commonTextFieldProps.SelectProps}
                                  >
                                    {getSubCategories(allocation.category).map(
                                      (sub) => (
                                        <MenuItem
                                          key={sub}
                                          value={sub}
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          {formatText(sub)}
                                        </MenuItem>
                                      )
                                    )}
                                  </Field>
                                  {getIn(
                                    formik.touched,
                                    `allocations.${index}.subCategory`
                                  ) &&
                                    getIn(
                                      formik.errors,
                                      `allocations.${index}.subCategory`
                                    ) && (
                                      <FormHelperText>
                                        {getIn(
                                          formik.errors,
                                          `allocations.${index}.subCategory`
                                        )}
                                      </FormHelperText>
                                    )}
                                </FormControl>
                              </Grid>

                              {/* Start Date */}
                              <Grid item xs={12} md={2}>
                                <Field
                                  as={TextField}
                                  name={`allocations.${index}.startDate`}
                                  label="Start Date"
                                  required
                                  type="date"
                                  fullWidth
                                  error={
                                    getIn(
                                      formik.touched,
                                      `allocations.${index}.startDate`
                                    ) &&
                                    Boolean(
                                      getIn(
                                        formik.errors,
                                        `allocations.${index}.startDate`
                                      )
                                    )
                                  }
                                  helperText={
                                    getIn(
                                      formik.touched,
                                      `allocations.${index}.startDate`
                                    ) &&
                                    getIn(
                                      formik.errors,
                                      `allocations.${index}.startDate`
                                    )
                                  }
                                  disabled={isSubmitting}
                                  {...commonDateFieldProps}
                                />
                              </Grid>

                              {/* Duration */}
                              <Grid item xs={12} md={1.5}>
                                <Field
                                  as={TextField}
                                  name={`allocations.${index}.durationMonths`}
                                  label="Duration (Months)"
                                  type="number"
                                  fullWidth
                                  placeholder="e.g., 6"
                                  inputProps={{ min: 1, max: 100 }}
                                  error={
                                    getIn(
                                      formik.touched,
                                      `allocations.${index}.durationMonths`
                                    ) &&
                                    Boolean(
                                      getIn(
                                        formik.errors,
                                        `allocations.${index}.durationMonths`
                                      )
                                    )
                                  }
                                  helperText={
                                    getIn(
                                      formik.touched,
                                      `allocations.${index}.durationMonths`
                                    ) &&
                                    getIn(
                                      formik.errors,
                                      `allocations.${index}.durationMonths`
                                    )
                                  }
                                  disabled={isSubmitting}
                                  onChange={(e: any) => {
                                    const durationStr = e.target.value;
                                    const duration = parseFloat(durationStr);
                                    formik.setFieldValue(
                                      `allocations.${index}.durationMonths`,
                                      durationStr
                                    );

                                    // Auto-calculate end date
                                    const startDate =
                                      formik.values.allocations[index]
                                        .startDate;
                                    if (
                                      startDate &&
                                      !isNaN(duration) &&
                                      duration > 0
                                    ) {
                                      const start = new Date(startDate);
                                      const end = new Date(start);
                                      end.setMonth(start.getMonth() + duration);
                                      formik.setFieldValue(
                                        `allocations.${index}.endDate`,
                                        end.toISOString().split("T")[0]
                                      );
                                    }
                                  }}
                                  {...commonTextFieldProps}
                                />
                              </Grid>

                              {/* Reference Selection */}
                              <Grid item xs={12}>
                                <ReferenceSelectionFields
                                  category={allocation.category}
                                  subCategory={allocation.subCategory}
                                  onReferenceChange={(reference, customData) =>
                                    handleReferenceChange(
                                      index,
                                      reference,
                                      customData,
                                      formik
                                    )
                                  }
                                  selectedReference={
                                    allocation.referenceData &&
                                    !allocation.referenceData.isCustom
                                      ? allocation.referenceData
                                      : null
                                  }
                                  disabled={isSubmitting}
                                />
                              </Grid>

                              {/* Meta Information */}
                              {![
                                AllocationCategory.RESOURCE,
                                AllocationCategory.VEHICLE,
                              ].includes(allocation.category) && (
                                <AllocationTagsSection
                                  allocationIndex={index}
                                  allocation={allocation}
                                  formik={formik}
                                  isSubmitting={isSubmitting}
                                  projectId={projectId}
                                />
                              )}
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Add Allocation Button */}
                      {formik.values.allocations.length < 10 && (
                        <Box sx={{ textAlign: "center", mt: 1.5, mb: 2 }}>
                          <Button
                            onClick={() => push({ ...initialAllocation })}
                            startIcon={<AddIcon />}
                            variant="outlined"
                            disabled={isSubmitting}
                            {...smallButtonProp}
                          >
                            Add Another {ProjectTabEnum.ALLOCATIONS.singular}
                          </Button>
                        </Box>
                      )}

                      {/* Form Errors */}
                      {formik.errors.allocations &&
                        typeof formik.errors.allocations === "string" && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {formik.errors.allocations}
                          </Alert>
                        )}

                      {/* Action Buttons */}
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1.5,
                        }}
                      >
                        <Button
                          onClick={goBack}
                          variant="outlined"
                          disabled={isSubmitting}
                          {...commonButtonProps}
                          color="error"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={
                            isSubmitting || !formik.isValid || !formik.dirty
                          }
                          {...commonButtonProps}
                        >
                          {isSubmitting
                            ? `Creating ${ProjectTabEnum.ALLOCATIONS.singular}s...`
                            : `Create`}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </FieldArray>
              </Paper>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateAllocation;
